window.season_adjust = {
  SELECTOR_MIN_ORDER_BLOCK: 'sterntaler-minimum-order-amount-block',
  SELECTOR_DYNAMIC_CONTENT_WRAPPER: '.dynamic-content-wrapper',
  
  // Request Deduplication State
  _isUpdating: false,
  _currentRetryCount: 0,
  _pendingPromise: null,
  _latestParams: null,
  
  // Cart operation tracking to prevent race conditions
  _pendingCartOperations: 0,
  _cartOperationListeners: [],

  // Dirty flag: true when cart was modified since last /cart/update
  _dirty: false,

  // Serialization queue: ensures only one cart mutation runs at a time (prevents 409 Conflict)
  _cartMutationQueue: Promise.resolve(),
  _MAX_409_RETRIES: 3,

  // Checkout buttons (cart page + cart drawer share this class)
  _CHECKOUT_BUTTON_SELECTOR: '.cart__checkout-button',

  /**
   * True for aborted / dropped fetches (navigation, ad-block, flaky network).
   * Faro treats console.error as an exception — do not log these as errors.
   */
  _isIgnorableFetchError: function(err) {
    if (!err) return false;
    const name = err.name || '';
    const msg = String(err.message || err);
    if (name === 'AbortError' || name === 'NetworkError') return true;
    return /NetworkError when attempting to fetch resource|Failed to fetch|Load failed|The operation was aborted|network error/i.test(msg);
  },

  // B2B Permission Error Detection
  _B2B_PERMISSION_ERROR_CODES: [
    'BUYER_CANNOT_PURCHASE_FOR_COMPANY_LOCATION',
    'COMPANY_LOCATION_REQUIRED',
    'LOST_ACCESS_TO_COMPANY',
    'COMPANY_PURCHASE_PERMISSION_REQUIRED',
    'PERSONAL_ORDER_NOT_ALLOWED'
  ],

  /**
   * Start tracking a cart operation (add/change).
   * Also locks the checkout button (STER-524) — a customer clicking "+" on a
   * quantity input must not be able to reach checkout before the fee
   * recalculation that follows (see `init()`) has even started.
   */
  _startCartOperation: function() {
    this._pendingCartOperations++;
    this._lockCheckoutButtons();
  },

  /**
   * End tracking a cart operation and notify listeners.
   * Only unlocks the checkout button once every in-flight cart operation
   * *and* every in-flight fee recalculation (`init()`) has finished — see
   * `_lockCheckoutButtons`/`_unlockCheckoutButtons` ref-counting.
   *
   * Guarded against unbalanced calls: some callers (quick-order-list.js,
   * sterntaler-quick-order-search.js) call this once on the success path and
   * again from a later catch that covers additional, unguarded work — a
   * failure there would otherwise call this twice per `_startCartOperation`,
   * driving the counter negative so it never returns to exactly 0 again and
   * permanently stalling `_waitForCartOperations()` (and with it every
   * subsequent `init()`, which would never reach its `finally` and never
   * unlock the checkout button). Treat a call with nothing pending as a
   * no-op instead of fixing every caller individually.
   */
  _endCartOperation: function() {
    if (this._pendingCartOperations === 0) return;

    this._pendingCartOperations--;
    this._unlockCheckoutButtons();

    if (this._pendingCartOperations === 0) {
      this._cartOperationListeners.forEach(callback => callback());
      this._cartOperationListeners = [];
    }
  },

  /**
   * Wait for all pending cart operations to complete
   */
  _waitForCartOperations: async function() {
    if (this._pendingCartOperations === 0) {
      return;
    }

    return new Promise((resolve) => {
      this._cartOperationListeners.push(resolve);
    });
  },

  /**
   * Fire-and-forget persist: notify the B2B App that the cart changed
   * so it can write the current cart into the customer metafield.
   */
  _persistCart: function() {
    try {
      const cartId = this.getCartIdFromCookie();
      if (!cartId || !window.routes.b2b_cart_persist) return;
      fetch(window.routes.b2b_cart_persist, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cartId }),
      }).catch(() => { /* fire-and-forget */ });
    } catch (e) { /* fire-and-forget */ }
  },

  // Ref count: how many overlapping operations currently need the checkout
  // button locked (a cart mutation and its follow-up fee recalculation both
  // hold a lock at once — see `_startCartOperation`/`init`). Only unlock once
  // this drops back to 0.
  _checkoutLockCount: 0,

  /**
   * Disable checkout buttons while a cart mutation (add/change/remove) or a
   * /cart/update fee recalculation is in flight.
   *
   * STER-524: the Mindermengenzuschlag (minimum order fee) is only kept in
   * sync with the cart total inside the /cart/update pass that follows every
   * cart mutation. If the customer starts checkout before that pass has run
   * — including the moment right after clicking "+", while only the native
   * /cart/change.js request and its per-item loading spinner are running —
   * Shopify carries a stale fee line into the checkout, and removing it
   * afterwards only affects the cart, not the checkout that already started.
   * Locking here (not just once the fee recalculation itself starts) closes
   * that whole window. Re-entrant: safe to call while already locked, and a
   * button already disabled for another reason (e.g. an empty cart) is left
   * untouched.
   */
  _lockCheckoutButtons: function() {
    this._checkoutLockCount++;
    document.querySelectorAll(this._CHECKOUT_BUTTON_SELECTOR).forEach((button) => {
      if (button.disabled) return;
      button.dataset.sternLockedByCartUpdate = 'true';
      button.dataset.sternOriginalText = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = `${window.seasonMapping.update} …`;
    });
  },

  /**
   * Release one lock taken by `_lockCheckoutButtons`. Re-enables the checkout
   * buttons only once no lock is outstanding any more.
   */
  _unlockCheckoutButtons: function() {
    this._checkoutLockCount = Math.max(0, this._checkoutLockCount - 1);
    if (this._checkoutLockCount > 0) return;

    document.querySelectorAll(this._CHECKOUT_BUTTON_SELECTOR).forEach((button) => {
      if (button.dataset.sternLockedByCartUpdate !== 'true') return;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = button.dataset.sternOriginalText;
      delete button.dataset.sternLockedByCartUpdate;
      delete button.dataset.sternOriginalText;
    });
  },

  /**
   * Client-side VE (Verpackungseinheit) quantity adjustment.
   * Rounds quantity to valid step multiples before sending to native Cart API.
   * @param {Number} quantity - desired quantity
   * @param {Number|null} step - VE step size (from data-step attribute)
   * @param {Number|null} min - minimum quantity (from min attribute)
   * @returns {Number} adjusted quantity
   */
  _adjustQuantityForVE: function(quantity, step, min) {
    if (!step || step <= 0) return quantity;
    const minVal = (min && min > 0) ? min : step;
    if (quantity <= 0) return 0;
    if (quantity < minVal) return minVal;
    if ((quantity - minVal) % step !== 0) {
      return minVal + Math.ceil((quantity - minVal) / step) * step;
    }
    return quantity;
  },

  /**
   * Check response data for B2B permission errors.
   * Inspects multiple response formats (userErrors, errors, error string, message).
   * @param {Object|String} data - Response data from B2B proxy
   * @returns {Object|null} Error object {code, message} if found, null otherwise
   */
  _checkForB2BErrors: function(data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      if (!parsed || typeof parsed !== 'object') return null;

      // Check userErrors array (Storefront API style)
      const userErrors = parsed.userErrors || parsed.user_errors;
      if (Array.isArray(userErrors)) {
        const b2bError = userErrors.find(err =>
          this._B2B_PERMISSION_ERROR_CODES.includes(err.code)
        );
        if (b2bError) {
          console.warn('[season_adjust] B2B permission error in userErrors:', b2bError);
          return { code: b2bError.code, message: b2bError.message };
        }
      }

      // Check errors array
      if (Array.isArray(parsed.errors)) {
        const b2bError = parsed.errors.find(err => {
          const errCode = err.code || '';
          const errMsg = err.message || '';
          return this._B2B_PERMISSION_ERROR_CODES.includes(errCode) ||
            this._B2B_PERMISSION_ERROR_CODES.some(code =>
              errMsg.toUpperCase().includes(code)
            );
        });
        if (b2bError) {
          console.warn('[season_adjust] B2B permission error in errors array:', b2bError);
          return { code: b2bError.code || 'UNKNOWN', message: b2bError.message };
        }
      }

      // Check top-level error string
      if (parsed.error && typeof parsed.error === 'string') {
        const upperError = parsed.error.toUpperCase();
        const matchedCode = this._B2B_PERMISSION_ERROR_CODES.find(code =>
          upperError.includes(code)
        );
        if (matchedCode) {
          console.warn('[season_adjust] B2B permission error in top-level error:', matchedCode);
          return { code: matchedCode, message: parsed.error };
        }
      }

      // Check top-level message string
      if (parsed.message && typeof parsed.message === 'string') {
        const upperMsg = parsed.message.toUpperCase();
        const matchedCode = this._B2B_PERMISSION_ERROR_CODES.find(code =>
          upperMsg.includes(code)
        );
        if (matchedCode) {
          console.warn('[season_adjust] B2B permission error in top-level message:', matchedCode);
          return { code: matchedCode, message: parsed.message };
        }
      }

      // Fallback: deep string search for known German/English error phrases
      const fullText = JSON.stringify(parsed).toLowerCase();
      const hasFallbackMatch = fullText.includes('kein kauf für diesen standort') ||
          fullText.includes('cannot purchase for company') ||
          fullText.includes('buyer_cannot_purchase');
      if (hasFallbackMatch) {
        console.warn('[season_adjust] B2B permission error via fallback deep-search');
        return {
          code: 'BUYER_CANNOT_PURCHASE_FOR_COMPANY_LOCATION',
          message: parsed.message || parsed.error || 'Kein Kauf für diesen Standort möglich'
        };
      }

      return null;
    } catch (e) {
      console.warn('[season_adjust] Exception in _checkForB2BErrors:', e);
      return null;
    }
  },

  /**
   * Show the B2B error banner at the top of the page.
   * Persists the error in sessionStorage so it survives navigation.
   * @param {String} message - Error message to display
   */
  _showB2BErrorBanner: function(message) {
    const DEBUG = '[season_adjust][_showB2BErrorBanner]';
    const defaultMessage = 'Warenkorb-Funktionen sind für Ihren Standort derzeit nicht verfügbar.';
    const displayMessage = message || defaultMessage;

    console.log(DEBUG, 'Aufgerufen mit Message:', displayMessage);

    const banner = document.getElementById('b2b-cart-permission-error');
    console.log(DEBUG, 'Banner DOM-Element gefunden:', !!banner, banner);
    if (banner) {
      //const messageEl = banner.querySelector('[data-b2b-error-message]');
      //console.log(DEBUG, 'Message-Element gefunden:', !!messageEl);
      //if (messageEl) {
      //  messageEl.textContent = displayMessage;
      //}
      banner.removeAttribute('hidden');
      console.log(DEBUG, 'Banner hidden-Attribut entfernt, Banner sollte jetzt sichtbar sein');
      console.log(DEBUG, 'Banner computed display:', window.getComputedStyle(banner).display);
      console.log(DEBUG, 'Banner computed visibility:', window.getComputedStyle(banner).visibility);
    } else {
      console.error(DEBUG, 'BANNER ELEMENT NICHT IM DOM! id="b2b-cart-permission-error" fehlt!');
    }

    try {
      sessionStorage.setItem('b2b_cart_permission_error', displayMessage);
    } catch (e) { /* sessionStorage not available */ }

    console.error('[season_adjust] B2B Cart Permission Error:', displayMessage);
  },

  /**
   * Hide the B2B error banner and clear sessionStorage.
   */
  _hideB2BErrorBanner: function() {
    const DEBUG = '[season_adjust][_hideB2BErrorBanner]';
    console.log(DEBUG, 'Aufgerufen - Banner verstecken');

    const banner = document.getElementById('b2b-cart-permission-error');
    if (banner) {
      banner.setAttribute('hidden', '');
      console.log(DEBUG, 'Banner hidden gesetzt');
    }

    try {
      sessionStorage.removeItem('b2b_cart_permission_error');
      console.log(DEBUG, 'sessionStorage cleared');
    } catch (e) { /* sessionStorage not available */ }
  },

  /**
   * Validate cart permissions on page load.
   * Uses Shopify-native /cart/update.js to detect if the cart is functional.
   * Shows banner with the actual Shopify error message if cart is blocked.
   */
  validateCartPermissions: async function() {
    const DEBUG = '[season_adjust][validateCartPermissions]';
    console.log(DEBUG, 'START');

    try {
      // Restore previously detected error from sessionStorage (instant feedback)
      try {
        const storedError = sessionStorage.getItem('b2b_cart_permission_error');
        console.log(DEBUG, 'sessionStorage:', storedError);
        if (storedError) {
          this._showB2BErrorBanner(storedError);
        }
      } catch (e) { /* sessionStorage not available */ }

      const cartId = this.getCartIdFromCookie();
      console.log(DEBUG, 'cartId:', cartId || '(leer)');
      if (!cartId) return;

      // Check if cart has items - only then can we validate permissions
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      console.log(DEBUG, '/cart.js item_count:', cartData?.item_count);

      // Shopify-native cart check (tests if Shopify can process the cart at all)
      console.log(DEBUG, 'POST /cart/update.js');
      const nativeResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: {} })
      });
      console.log(DEBUG, 'Status:', nativeResponse.status);

      if (!nativeResponse.ok) {
        const nativeBody = await nativeResponse.text();
        console.error(DEBUG, 'FEHLER Body:', nativeBody.substring(0, 500));
        let errorMsg = 'Warenkorb ist derzeit nicht erreichbar.';
        try {
          const parsed = JSON.parse(nativeBody);
          errorMsg = parsed.message || parsed.description || errorMsg;
        } catch (e) { /* not JSON */ }
        this._showB2BErrorBanner(errorMsg);
        return;
      }

      const nativeData = await nativeResponse.json();
      console.log(DEBUG, 'OK - item_count:', nativeData?.item_count);
      this._hideB2BErrorBanner();

    } catch (err) {
      if (this._isIgnorableFetchError(err)) return;
      console.error(DEBUG, 'EXCEPTION:', err);
    }
  },

  /**
   * Get cart ID from cookie and return it in GID format
   */
  getCartIdFromCookie: function() {
    try {
      const cookie = document.cookie;

      const cartCookie = cookie.split('; ').find(row => row.startsWith('cart='));
      if (!cartCookie) return '';
      const cartIdRaw = cartCookie.split('=')[1] || '';
      if (!cartIdRaw) return '';
      const cartIdDecoded = decodeURIComponent(cartIdRaw);
      if (!cartIdDecoded) return '';
      return `gid://shopify/Cart/${cartIdDecoded}`;
    } catch (err) {
      return '';
    }
  },

  /**
   * Validate cart ID on page load and initialize cart if not present
   * Uses /cart/update.js to create a cart if none exists
   * @returns {Promise<string>} Cart ID in GID format or empty string
   */
  validateCartId: async function() {
    try {
      // First try to get from cookie
      const cartIdFromCookie = this.getCartIdFromCookie();
      if (cartIdFromCookie) {
        // cart id from cookie, everything is fine
        return cartIdFromCookie;
      }
      
      // Trigger cart creation with empty update (doesn't add items)
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates: {} })
      });
      
      if (!updateResponse.ok) {
        console.error('[season_adjust] Cart update failed:', updateResponse.status);
        return '';
      }
      
      const cartData = await updateResponse.json();
      const cartToken = cartData?.token;
      if (!cartToken) {
        console.error('[season_adjust] Cart Token not available!');
        return '';
      }
      
      const cartId = `gid://shopify/Cart/${cartToken}`;
      console.log('[season_adjust] Cart ID after initialization:', cartId);
      return cartId;
      
    } catch (err) {
      if (!this._isIgnorableFetchError(err)) {
        console.error('[season_adjust] Error validating cart ID:', err);
      }
      return '';
    }
  },


  /**
   * Initialize cart update request with race condition prevention.
   * Uses request deduplication and always processes the latest parameters.
   */
  init: async function(render = false, reload = false, variantIds = null) {
    this._latestParams = { render, reload, variantIds };

    if (this._isUpdating && this._pendingPromise) {
      // Reset retry counter on new requests during retry phase to give backend more time
      this._currentRetryCount = 0;
      return this._pendingPromise;
    }

    this._isUpdating = true;
    this._currentRetryCount = 0;

    // Ensure cart ID is available before proceeding
    await this.validateCartId();

    this._pendingPromise = (async () => {
      try {
        // Locked and unlocked in the same try/finally so a future throw in
        // validateCartId() above can never leave the checkout button stuck.
        this._lockCheckoutButtons();
        while (true) {
          // Wait for all pending cart operations to prevent race conditions
          await this._waitForCartOperations();
          
          const operationsBeforeUpdate = this._pendingCartOperations;
          const params = this._latestParams || { render, reload, variantIds };
          
          const cartData = await this._performUpdate(
            params.render, 
            params.reload, 
            params.variantIds
          );
          
          if (this._pendingCartOperations === 0 && operationsBeforeUpdate === 0) {
            return cartData;
          } else if (this._pendingCartOperations > 0) {
            continue;
          } else {
            return cartData;
          }
        }
      } catch (error) {
        console.error('[season_adjust] Error in update:', error);
        throw error;
      } finally {
        this._isUpdating = false;
        this._pendingPromise = null;
        this._latestParams = null;
        this._unlockCheckoutButtons();
      }
    })();

    return this._pendingPromise;
  },

  /**
   * Perform cart update request with retry logic
   * @private
   */
  _performUpdate: async function(render, reload, variantIds) {
    const MAX_RETRIES = 50;
    const RETRY_DELAY = 1000;

    // Skip update if cart error banner is already visible (cart is blocked)
    const banner = document.getElementById('b2b-cart-permission-error');
    if (banner && !banner.hasAttribute('hidden')) {
      console.log('[season_adjust][_performUpdate] Banner sichtbar, ueberspringe Update');
      return;
    }

    try {
      const bodyObj = { cartId: this.getCartIdFromCookie() };
      const bodyObjString = JSON.stringify(bodyObj);

      const response = await fetch(window.routes.b2b_cart_update, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: bodyObjString
      });

      const data = await response.text();
      
      if (response.status === 200) {
        let parsedData;
        try {
          parsedData = JSON.parse(data);
        } catch (e) {
          parsedData = data;
        }

        // Check for B2B permission errors BEFORE retry logic
        const b2bError = this._checkForB2BErrors(parsedData);
        if (b2bError) {
          this._showB2BErrorBanner(b2bError.message);
          this._currentRetryCount = 0;
          return parsedData;
        }

        const shouldRetry = parsedData.is_processing === true || 
                           (parsedData.success === false && 
                            (parsedData.error === 'Unable to process cart operation' || 
                             parsedData.error?.includes('Unable to process')));
        
        if (shouldRetry) {
          this._currentRetryCount++;
          
          if (this._currentRetryCount < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
            return await this._performUpdate(render, reload, variantIds);
          } else {
            const reason = parsedData.is_processing ? 'still processing' : `error: ${parsedData.error}`;
            console.error(`[season_adjust] Max retries reached, last status: ${reason}`);
            this._currentRetryCount = 0;
            return;
          }
        }

        this._currentRetryCount = 0;
        this._dirty = false;
        
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        
        // Dispatch event with cartData to prevent race conditions in UI updates
        const cartUpdateEvent = new CustomEvent('cart:updated', {
          detail: {
            data: parsedData,
            cartData: cartData,
            variantIds: variantIds,
            render: render,
            reload: reload
          }
        });
        document.dispatchEvent(cartUpdateEvent);
        
        if (render && typeof this.handleCartUpdate === 'function') {
          this.handleCartUpdate(
            typeof parsedData === "string" ? parsedData : JSON.stringify(parsedData),
            variantIds
          );
        } else if (reload) {
          window.location.reload();
        }
        
        return cartData;
      } else {
        console.error("[season_adjust] Error updating cart", data);
        this._currentRetryCount = 0;
      }
    } catch (error) {
      console.error("[season_adjust] Fehler beim Cart-Update-Request:", error);
      this._currentRetryCount = 0;
      throw error;
    }
  },

  /**
   * Process cart update response and render total-cart minimum order value display.
   * Calculates the total cart value client-side and compares against shop_minimum_order_value.
   */
  handleCartUpdate: function(body, variantIds) {
    try {
      const parsedState = JSON.parse(body);

      const orderSplitDisabled = parsedState.order_split_disabled || 'false';
      if (orderSplitDisabled === 'true') {
        return;
      }

      const template = document.querySelector(this.SELECTOR_MIN_ORDER_BLOCK);
      if (!template) {
        return;
      }
      const wrapper = template.querySelector(this.SELECTOR_DYNAMIC_CONTENT_WRAPPER);
      if (!wrapper) {
        return;
      }
      wrapper.innerHTML = '';

      const minOrderValueCents = parseInt(template.dataset.minOrderValueCents, 10);
      if (isNaN(minOrderValueCents) || minOrderValueCents <= 0) {
        return;
      }

      // Fee-Item Variant-ID (Mindermengenzuschlag)
      const FEE_VARIANT_ID = 54804919943512;

      // Calculate total cart value from all non-fee items
      let totalCartValue = 0;
      if (parsedState.season_groups && Array.isArray(parsedState.season_groups)) {
        for (const group of parsedState.season_groups) {
          totalCartValue += group.total_value || 0;
        }
      }

      const requirementMet = totalCartValue >= minOrderValueCents;
      const percentage = minOrderValueCents > 0 ? Math.min((totalCartValue / minOrderValueCents * 100), 100).toFixed(2) : 0;
      const remaining = minOrderValueCents - totalCartValue;
      const formattedRemaining = (remaining / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
      const requirementText = requirementMet
        ? 'Mindestbestellwert erreicht!'
        : window.seasonMapping.toOrderWithoutLowStock.replace("[value]", formattedRemaining);

      const progressDiv = document.createElement("div");
      progressDiv.className = `season-progress${requirementMet ? ' requirement-met' : ''}`;
      progressDiv.innerHTML = `
        <strong class="season-display-name">Gesamter Warenkorb</strong>
        <p class="order-requirement-text">${requirementText}</p>
        <div class="progress-bar">
          <div class="progress" style="width: ${percentage}%;"></div>
        </div>
      `;
      wrapper.appendChild(progressDiv);
    } catch (error) {
      console.error("[season_adjust] Error handling cart update", error, body);
    }
  },

  /**
   * Cart Add - Serialized via queue to prevent 409 Conflict
   */
  cartAdd: function(items, sections, sectionsUrl) {
    const prev = this._cartMutationQueue;
    const next = prev
      .catch(() => {})
      .then(() => this._doCartAdd(items, sections, sectionsUrl));
    this._cartMutationQueue = next.catch(() => {});
    return next;
  },

  /**
   * @private Cart Add implementation with 409 retry
   */
  _doCartAdd: async function(items, sections = null, sectionsUrl = null) {
    this._startCartOperation();

    try {
      for (let attempt = 0; attempt <= this._MAX_409_RETRIES; attempt++) {
        const body = { items: items };

        if (sections) {
          body.sections = sections;
        }
        if (sectionsUrl) {
          body.sections_url = sectionsUrl;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });

        if (response.status === 409 && attempt < this._MAX_409_RETRIES) {
          console.warn('[season_adjust] 409 Conflict on /cart/add.js, retry ' + (attempt + 1) + '/' + this._MAX_409_RETRIES);
          await new Promise(resolve => setTimeout(resolve, 300 * (attempt + 1)));
          continue;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const b2bError = this._checkForB2BErrors(errorData);
          if (b2bError) throw new Error(b2bError.message);
          throw new Error(errorData.description || errorData.message || 'Artikel konnte nicht hinzugefügt werden.');
        }

        const result = await response.json();

        const b2bError = this._checkForB2BErrors(result);
        if (b2bError) throw new Error(b2bError.message);

        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();

        if (result.sections) {
          cartData.sections = result.sections;
        } else if (sections && sections.length > 0) {
          const sectionsToFetch = sections.join(',');
          const sectionsResponse = await fetch((sectionsUrl || window.location.pathname) + '?sections=' + sectionsToFetch);
          const sectionsText = await sectionsResponse.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionsText, 'text/html');

          cartData.sections = {};
          sections.forEach((sectionId) => {
            const sectionElement = doc.getElementById(sectionId);
            if (sectionElement) {
              cartData.sections[sectionId] = sectionElement.outerHTML;
            }
          });
        }

        this._dirty = true;
        this._persistCart();

        return cartData;
      }
    } catch (error) {
      if (!this._isIgnorableFetchError(error)) {
        console.error('[season_adjust] Error in cartAdd:', error);
      }
      throw error;
    } finally {
      this._endCartOperation();
    }
  },

  /**
   * Cart Change - Serialized via queue to prevent 409 Conflict
   */
  cartChange: function(lineId, quantity, sections, sectionsUrl) {
    const prev = this._cartMutationQueue;
    const next = prev
      .catch(() => {})
      .then(() => this._doCartChange(lineId, quantity, sections, sectionsUrl));
    this._cartMutationQueue = next.catch(() => {});
    return next;
  },

  /**
   * @private Cart Change implementation with 409 retry
   */
  _doCartChange: async function(lineId, quantity, sections = null, sectionsUrl = null) {
    this._startCartOperation();

    try {
      for (let attempt = 0; attempt <= this._MAX_409_RETRIES; attempt++) {
        const body = {
          id: String(lineId),
          quantity: parseInt(quantity),
        };

        if (sections) {
          body.sections = sections;
        }
        if (sectionsUrl) {
          body.sections_url = sectionsUrl;
        }

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });

        if (response.status === 409 && attempt < this._MAX_409_RETRIES) {
          console.warn('[season_adjust] 409 Conflict on /cart/change.js, retry ' + (attempt + 1) + '/' + this._MAX_409_RETRIES);
          await new Promise(resolve => setTimeout(resolve, 300 * (attempt + 1)));
          continue;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const b2bError = this._checkForB2BErrors(errorData);
          if (b2bError) throw new Error(b2bError.message);
          throw new Error(errorData.description || errorData.message || 'Warenkorb konnte nicht aktualisiert werden.');
        }

        const cartData = await response.json();

        const b2bError = this._checkForB2BErrors(cartData);
        if (b2bError) throw new Error(b2bError.message);

        this._dirty = true;
        this._persistCart();

        return cartData;
      }
    } catch (error) {
      if (!this._isIgnorableFetchError(error)) {
        console.error('[season_adjust] Error in cartChange:', error);
      }
      throw error;
    } finally {
      this._endCartOperation();
    }
  }
}

// Validate cart ID and check B2B permissions on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.season_adjust.validateCartId().then(() => {
      // Non-blocking permission check after cart ID is validated
      window.season_adjust.validateCartPermissions();
    });
  });
} else {
  // DOM already loaded
  window.season_adjust.validateCartId().then(() => {
    window.season_adjust.validateCartPermissions();
  });
}